import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { getScannedDocuments } from "_controllers/officer";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Toaster } from "react-hot-toast";
import { toastMessage } from "utils/toastMessage";
import authAxios from "setups/axios";
import { useDispatch, useSelector } from "react-redux";
import { fetchOfficersList } from "slices/officers";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AttachmentIcon from "@mui/icons-material/Attachment";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { schemeConstants } from "config/constants";
import * as XLSX from "xlsx";
import writeXlsxFile from "write-excel-file";
import { PageHeader, SectionCard, EmptyState, TablePager } from "_shared/ui";

const messageStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75%",
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 2,
  textAlign: "center",
};

// Shared style for centered themed modal surfaces (square corners per theme).
const modalSurface = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: { xs: 3, sm: 4 },
  textAlign: "left",
};

const Main = () => {
  const dispatch = useDispatch();
  const { officers } = useSelector((state) => state.officers);
  const { user } = useSelector((state) => state.user);

  let sampleFile = `https://jkmsfa.s3.amazonaws.com/public/1713864902326-0.6080237631550627.xlsx`;

  useEffect(() => {
    if (!user?._id) return;
    dispatch(fetchOfficersList(user.SAP_ID));
  }, [user]);

  let updatedUsers = officers?.map((user) => ({
    SAP_ID: user?.SAP_ID,
    scanEnabled: user?.scanEnabled,
    areaName: user?.areaName,
    areaCode: user?.areaCode,
    regionName: user?.regionName,
    regionCode: user?.regionCode,
    name: user?.name,
    zone: user?.zone,
    territoryName: user?.territories[0]?.name,
    territoryCode: user?.territories[0]?.territoryCode,
  }));

  // console.log("updated", JSON.stringify(updatedUsers.slice(-3)));

  const [data, setData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [image, setImage] = useState("");

  const [schemeData, setSchemeData] = useState();

  const [categories, setCategories] = useState([]);
  const [spendLimit, setSpendLimit] = useState([]);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [docListOpen, setDocListOpen] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");

  const userType = [
    { value: "SO", name: "Sales Officer" },
    { value: "AO", name: "Area Manager" },
    { value: "RO", name: "Regional Manager" },
    { value: "ZO", name: "Zonal Manager" },
  ];

  let columns = useMemo(() => [
    {
      headerName: "Filled Date",
      flex: 1,
    },
    {
      headerName: "Filled By",
      flex: 1,
    },
    // {
    //   headerName: "Uploaded On",
    //   flex: 2,
    // },
    {
      headerName: "#Files",
      flex: 2,
    },
    {
      headerName: "Action",
      flex: 1,
    },
  ]);

  useEffect(async () => {
    const survey = await getScannedDocuments({
      currentPage,
      searchUser,
    });
    setData(survey?.data);
    setMessage(survey?.message);
    setLoading(true);
    setTotalPages(survey?.totalPage ?? 1);
  }, [message, currentPage, searchUser]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const [formData, setFormData] = useState([]);

  const handleSubmit = async () => {
    const response = await authAxios()({
      url: `/api/v1/scan/user/access`,
      method: "PUT",
      data: selectedUsers,
    });
    toastMessage(response?.data?.message, "success");
  };

  useEffect(() => {
    let updatedCategories = categories.map((category) => {
      let roSpendLimit = spendLimit.find((obj) => obj.role === role);
      if (roSpendLimit) {
        let updatedSubCategory = category.subCategory.map((subCategory) => {
          let matchedSubCategory = roSpendLimit.spendLimit
            .find((s) => s.category === category._id)
            ?.subCategory.find((sub) => sub.subCategory === subCategory._id);
          if (matchedSubCategory) {
            return { ...subCategory, amount: matchedSubCategory.amount };
          }
          return subCategory;
        });
        return { ...category, subCategory: updatedSubCategory };
      }
      return category;
    });
    setCategories(updatedCategories);
  }, [role]);

  const handleViewClose = () => {
    setView(false);
    setDocListOpen(false);
  };

  const handleOpenClose = () => {
    setOpen(false);
  };

  const saveImg = async (e) => {
    setImage(e.target.files[0]);
  };

  const uploadDocument = async (e) => {
    e.preventDefault();
    if (!image) {
      // alert("Please select a file to upload.");
      setErrMsg("Please select a file to upload.");
      return;
    }
    const formData = new FormData();
    formData.append("file", image);
    formData.append("fileName", image.name);
    const token = localStorage.getItem("token");
    const config = {
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    const url = process.env.REACT_APP_REQUEST_ENDPOINT;
    await axios
      .put(`${url}/api/v1/scan/upload?id=${schemeData?._id}`, formData, config)
      .then((res) => {
        setMessage(res?.data?.message);
        toastMessage(res?.data?.message, "success");
        setUploadModal(false);
      })
      .catch((err) => {
        setImage("");
        setMessage(err?.message);
        toastMessage("Select File First.", "error");
        setUploadModal(false);
      });
  };

  const updatedString = (str) => {
    return Object?.entries(schemeConstants).reduce((acc, [key, value]) => {
      const regex = new RegExp(key, "gi");
      return acc.replace(regex, value);
    }, str);
  };

  const jsonToExcel = (json) => {
    console.log("working", JSON.parse(json));
    let fileName = "survey.xlsx";
    // const worksheet = XLSX.utils.json_to_sheet(json);
    // const workbook = XLSX.utils.book_new();
    // XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    // XLSX.writeFile(workbook, "data.xlsx");
    let excelData = [];

    for (const [key, value] of Object.entries(JSON.parse(json))) {
      if (typeof value.Rows === "object") {
        console.log("working2", key, value);
        excelData.push([
          {
            value: value?.Name,
          },
        ]);
        let head = [];
        for (let i = 0; i < value?.Headers.length; i++) {
          head.push({
            value: value?.Headers[i],
          });
        }
        excelData.push(head);
        for (let i = 0; i < value.Rows.length; i++) {
          let ar = [];
          for (let j = 0; j < value.Rows[i].length; j++) {
            ar.push({
              value: value.Rows[i][j],
            });
          }
          excelData.push(ar);
        }
      } else {
        console.log("working1");
        excelData.push([{ value: key }, { value }]);
      }
    }
    const downloadXlsxFile = () => {
      writeXlsxFile(excelData, {
        fileName: fileName,
      });
    };
    downloadXlsxFile();
  };

  const handleReload = () => {
    setMessage("Survey Updated");
  };

  console.log("view", image);

  console.log("updated", errMsg);

  return (
    <>
      <PageHeader
        title="Market Survey Reports - Reconciled"
        subtitle="Review reconciled survey submissions and upload modified files"
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => window.open(sampleFile)}
          >
            Download Sample
          </Button>
        }
      />

      <SectionCard sx={{ mb: 3 }}>
        <TextField
          size="small"
          onChange={(e) => setSearchUser(e.target.value)}
          value={searchUser}
          name="searchUser"
          variant="outlined"
          label="Search by SAP ID."
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </SectionCard>

      <SectionCard disablePadding>
        <TableContainer sx={{ maxHeight: "68vh" }}>
          <Table stickyHeader>
            {columns && columns.length > 0 ? (
              <TableHead>
                <TableRow>
                  {columns.map((item, idx) => (
                    <TableCell
                      align={idx === 0 ? "left" : "center"}
                      sx={{ fontWeight: 700 }}
                      key={idx}
                    >
                      {item.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            ) : null}

            <TableBody>
              {data && data?.length > 0 ? (
                data?.map((d) => (
                  <TableRow key={d?._id} hover>
                    <TableCell align="left">
                      {d?.documents
                        ? moment
                            .utc(d?.documents[0]?.scanDate)
                            .format("YYYY-MM-DD")
                        : "-"}
                    </TableCell>
                    <TableCell align="center">{d?._id ?? "-"}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label={d?.documents ? d?.documents?.length : 0}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<FileUploadIcon />}
                        onClick={() => {
                          setDocListOpen(true);
                          setSchemeData(d);
                        }}
                      >
                        Upload
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns?.length || 4} sx={{ border: 0 }}>
                    <EmptyState
                      title="No data"
                      description="No reconciled survey reports are available yet."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePager
          totalPages={totalPages}
          page={currentPage}
          onPageChange={(p) => handlePageChange(null, p)}
        />
      </SectionCard>

      {/* {view ? (
        <SchemeView
          view={view}
          handleViewClose={handleViewClose}
          data={schemeData}
          handleReload={handleReload}
        />
      ) : null} */}

      <Modal
        open={uploadModal}
        onClose={() => {
          setUploadModal(false);
          setErrMsg("");
        }}
      >
        <Box sx={{ ...modalSurface, width: { xs: "90%", sm: "40%" } }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Please upload modified survey file.
            </Typography>
            <TextField
              size="small"
              onChange={saveImg}
              InputProps={{
                disableUnderline: true,
              }}
              type={"file"}
              id="standard-basic"
              label="Image"
              name="image"
              variant="standard"
              error={errMsg !== "" ? true : false}
              helperText={errMsg !== "" ? errMsg : ""}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 1,
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setUploadModal(false);
                  setImage("");
                  setErrMsg("");
                }}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={uploadDocument}>
                Submit
              </Button>
            </Box>
          </Stack>
        </Box>
      </Modal>

      <Modal
        open={docListOpen}
        onClose={() => {
          setDocListOpen(false);
        }}
      >
        <Box sx={{ ...modalSurface, width: { xs: "94%", md: "75%" }, p: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upload and View Survey Files.
            </Typography>
            <IconButton
              aria-label="close"
              color="error"
              onClick={() => setDocListOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ px: 3, pb: 3 }}>
            <TableContainer sx={{ maxHeight: "70vh" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: 700 }}>
                      S.No
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      File Name
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Uploaded On
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Actions
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Downloaded On
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {schemeData?.documents &&
                  schemeData?.documents?.length > 0 ? (
                    schemeData?.documents?.map((d, idx) => (
                      <TableRow key={d?._id} hover>
                        <TableCell align="left">{idx + 1}</TableCell>
                        <TableCell align="center">
                          {updatedString(d?.documentName) ?? "-"}
                        </TableCell>
                        <TableCell align="center">
                          {moment.utc(d?.scanDate).format("YYYY-MM-DD") ?? "-"}
                        </TableCell>

                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setUploadModal(true);
                              setDocListOpen(false);
                              setSchemeData(d);
                            }}
                          >
                            <FileUploadIcon />
                          </IconButton>
                          <IconButton
                            color="primary"
                            component="a"
                            href={d?.uploadedDocument}
                            target="_blank"
                            download
                            disabled={
                              d?.uploadedDocument === null ||
                              d?.uploadedDocument === undefined
                                ? true
                                : false
                            }
                          >
                            <DownloadIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell align="center">
                          {moment.utc(d?.scanDate).format("YYYY-MM-DD") ?? "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ border: 0 }}>
                        <EmptyState
                          title="No data"
                          description="No survey files have been added yet."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

const SurevyReconcilation = () => {
  return (
    <AccessController roles={["ADMIN", "AO"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default SurevyReconcilation;
